import React, { useEffect, useState, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'

import './ai-styles.css'

/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/label-has-associated-control */

const API_HOST = process.env.REACT_APP_API_HOST

// Message component
function Message({ message }) {
  const { content, sender } = message

  const formatContent = (text) => ({
    __html: text
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>'),
  })

  return (
    <div className={`message message-${sender}`}>
      <div className="message-content" dangerouslySetInnerHTML={formatContent(content)} />
    </div>
  )
}

// Loading indicator component
function LoadingIndicator() {
  return (
    <div className="message message-ai">
      <div className="message-content">
        <span className="loading">Thinking</span>
      </div>
    </div>
  )
}

// Chat panel component
function ChatPanel({
  messages,
  input,
  setInput,
  handleSend,
  isLoading,
  model,
  setModel,
  dataSource,
  setDataSource,
  dataSources,
  messagesEndRef,
}) {
  // Handle keyboard events in the textarea
  const handleKeyDown = (e) => {
    // Check for Enter without shift key
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault() // Prevent default to avoid newline
      if (!isLoading && input.trim()) {
        handleSend(e)
      }
    }
  }
  return (
    <div className="chat-container">
      <div className="chat-body">
        {messages.map((msg, index) => (
          <Message key={`message-${msg.sender}-${index}-${msg.content.slice(0, 20)}`} message={msg} />
        ))}
        {isLoading && <LoadingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <form onSubmit={handleSend}>
          <div className="row g-3 align-items-end">
            <div className="col-md-10">
              <div className="mb-2">
                <textarea
                  className="form-control"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows="2"
                  placeholder="Ask me about GiveWise user data... (Press Enter to send)"
                  disabled={isLoading}
                />
                <small className="text-muted">Press Enter to send, Shift+Enter for new line</small>
              </div>

              <div className="row g-2">
                <div className="col-6">
                  <label htmlFor="data-source-select" className="form-label small">
                    Data Source
                  </label>
                  <select
                    id="data-source-select"
                    className="form-select form-select-sm"
                    value={dataSource}
                    onChange={(e) => setDataSource(e.target.value)}
                    disabled={isLoading}
                  >
                    {dataSources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-6">
                  <label htmlFor="ai-model-select" className="form-label small">
                    AI Model
                  </label>
                  <select
                    id="ai-model-select"
                    className="form-select form-select-sm"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="ClaudeApiConfig3.5-haiku">Claude 3.5 Haiku</option>
                    <option value="ClaudeApiConfig3.7-sonnet">Claude 3.7 Sonnet</option>
                    <option value="GPT-API-4o">GPT-4o</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary w-100" disabled={isLoading} style={{ height: '38px' }}>
                Send
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

// Admin Panel component
function AdminPanel({ onReindex, onClearChat, dataSources }) {
  const [preview, setPreview] = useState('(No data loaded)')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedSource, setSelectedSource] = useState('all')
  const [stats, setStats] = useState(null)

  const handleLoadPreview = async () => {
    setIsLoading(true)
    try {
      const res = await fetch(`${API_HOST}/debug/qdrant-preview?dataSource=${selectedSource}`)
      const result = await res.text()
      setPreview(result)
    } catch (error) {
      setPreview(`Error loading preview: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_HOST}/debug/qdrant-stats`)
      const data = await res.json()
      setStats(data)
    } catch (error) {
      console.error('Failed to load stats:', error)
    }
  }

  // Load stats on component mount
  useEffect(() => {
    loadStats()
  }, [])

  return (
    <div className="admin-container">
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Database Statistics</h5>
            </div>
            <div className="card-body">
              {stats ? (
                <div>
                  <p>Last updated: {new Date(stats.timestamp).toLocaleString()}</p>
                  <div className="row">
                    <div className="col-md-6">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Data Source</th>
                            <th>Records</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(stats.counts).map(([source, count]) => (
                            <tr key={source}>
                              <td>
                                {source === 'total'
                                  ? 'Total'
                                  : source.charAt(0).toUpperCase() + source.slice(1).replace(/([A-Z])/g, ' $1')}
                              </td>
                              <td>{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <button type="button" onClick={loadStats} className="btn btn-outline-primary btn-sm">
                        🔄 Refresh Stats
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p>Loading stats...</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Reindex Operations</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="input-group mb-3">
                    <label htmlFor="admin-data-source-select" className="input-group-text">
                      Data Source
                    </label>
                    <select
                      id="admin-data-source-select"
                      className="form-select"
                      value={selectedSource}
                      onChange={(e) => setSelectedSource(e.target.value)}
                      disabled={isLoading}
                    >
                      {dataSources.map((source) => (
                        <option key={source.id} value={source.id}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <button
                      type="button"
                      onClick={() => onReindex(selectedSource)}
                      className="btn btn-warning me-2"
                      disabled={isLoading}
                    >
                      🔄 Reindex Selected Source
                    </button>
                    <button type="button" onClick={onClearChat} className="btn btn-danger" disabled={isLoading}>
                      🗑️ Clear Chat
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Data Preview</h5>
              <button
                type="button"
                onClick={handleLoadPreview}
                className="btn btn-outline-secondary btn-sm"
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : '📊 Load Preview'}
              </button>
            </div>
            <div className="card-body">
              <pre style={{ maxHeight: '400px', overflowY: 'auto' }}>{preview}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Tab component
function Tabs({ activeTab, setActiveTab }) {
  const handleTabClick = (tabName, e) => {
    e.preventDefault()
    setActiveTab(tabName)
  }

  return (
    <ul className="nav nav-tabs mb-4">
      <li className="nav-item">
        <button
          type="button"
          className={`nav-link ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={(e) => handleTabClick('chat', e)}
          style={{ border: 'none', background: 'none' }}
        >
          💬 Chat
        </button>
      </li>
      <li className="nav-item">
        <button
          type="button"
          className={`nav-link ${activeTab === 'admin' ? 'active' : ''}`}
          onClick={(e) => handleTabClick('admin', e)}
          style={{ border: 'none', background: 'none' }}
        >
          ⚙️ Admin Tools
        </button>
      </li>
    </ul>
  )
}

// Main Chat App component
function ChatApp() {
  const link = document.createElement('link')
  link.href = 'https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css'
  link.rel = 'stylesheet'
  document.head.appendChild(link)

  // Fetch data sources from API
  const [dataSources, setDataSources] = useState([
    { id: 'users', name: 'Users', description: 'User accounts and information' },
    { id: 'funds', name: 'Funds', description: 'Giving funds data' },
    { id: 'charityUsers', name: 'Charity Users', description: 'Users associated with charities' },
    { id: 'managingUserOnFund', name: 'Managing Users on Funds', description: 'Fund management relationships' },
    { id: 'all', name: 'All Data Sources', description: 'Search across all data sources' },
  ])

  const [activeTab, setActiveTab] = useState('chat')
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      content:
        "Hello! I'm the GiveWise AI assistant. I can help you analyze user data and answer questions about GiveWise. What would you like to know?",
    },
  ])
  const [input, setInput] = useState('')
  const [model, setModel] = useState('GPT-API-4o') // Default to GPT-4o
  const [dataSource, setDataSource] = useState('all') // Default to search all sources
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef(null)

  // Fetch data sources - moved to useCallback to fix hoisting issue
  const fetchDataSources = useCallback(async () => {
    try {
      const res = await fetch(`${API_HOST}/data-sources`)
      const sources = await res.json()
      setDataSources(sources)
    } catch (error) {
      console.error('Failed to fetch data sources:', error)
    }
  }, [])

  // Fetch data sources on component mount
  useEffect(() => {
    fetchDataSources()
  }, [fetchDataSources])

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = async (e) => {
    e.preventDefault()

    if (!input.trim()) return

    // Add user message
    setMessages((prev) => [...prev, { sender: 'user', content: input }])
    setInput('')
    setIsLoading(true)

    try {
      const res = await fetch(`${API_HOST}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: input,
          modelKey: model,
          dataSource,
        }),
      })

      const result = await res.json()

      // Add AI response
      setMessages((prev) => [...prev, { sender: 'ai', content: result.response }])
    } catch (error) {
      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          content: `Sorry, an error occurred: ${error.message}`,
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleReindex = async (source) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Are you sure you want to reindex the "${source}" data source from API reports? This may take a moment.`
      )
    )
      return

    setActiveTab('chat') // Switch to chat tab to show the status

    setMessages((prev) => [
      ...prev,
      {
        sender: 'ai',
        content: `📡 Starting reindex for "${source}" data source from API reports...`,
      },
    ])

    try {
      const res = await fetch(`${API_HOST}/reindex-from-api`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataSource: source }),
      })

      const result = await res.json()

      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          content: `✅ Successfully reindexed "${source}" data source from API reports!`,
        },
      ])
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          content: `❌ Error during reindexing "${source}" data source: ${error.message}`,
        },
      ])
    }
  }

  const handleClearChat = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to clear the chat history?')) return

    setMessages([
      {
        sender: 'ai',
        content:
          "Hello! I'm the GiveWise AI assistant. I can help you analyze user data and answer questions about GiveWise. What would you like to know?",
      },
    ])

    setActiveTab('chat') // Switch to chat tab after clearing
  }

  const getModelName = () => {
    switch (model) {
      case 'ClaudeApiConfig3.5-haiku':
        return 'Claude 3.5 Haiku'
      case 'ClaudeApiConfig3.7-sonnet':
        return 'Claude 3.7 Sonnet'
      case 'GPT-API-4o':
        return 'GPT-4o'
      default:
        return model
    }
  }

  const getSourceName = () => {
    const source = dataSources.find((s) => s.id === dataSource)
    return source ? source.name : dataSource
  }

  return (
    <div className="ai-chat-app">
      <div className="container mt-4 mb-5">
        <div className="app-header mb-4">
          <h1>GiveWise AI Assistant</h1>
          <div className="d-flex">
            <span className="model-badge me-2">{getModelName()}</span>
            <span className="source-badge">{getSourceName()}</span>
          </div>
        </div>

        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

        {activeTab === 'chat' ? (
          <ChatPanel
            messages={messages}
            input={input}
            setInput={setInput}
            handleSend={handleSend}
            isLoading={isLoading}
            model={model}
            setModel={setModel}
            dataSource={dataSource}
            setDataSource={setDataSource}
            dataSources={dataSources}
            messagesEndRef={messagesEndRef}
          />
        ) : (
          <AdminPanel onReindex={handleReindex} onClearChat={handleClearChat} dataSources={dataSources} />
        )}
      </div>
    </div>
  )
}

// Render the app
if (typeof document !== 'undefined') {
  const rootElement = document.getElementById('root')
  if (rootElement) {
    ReactDOM.render(<ChatApp />, rootElement)
  }
}

export default ChatApp
